import { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { request } from "../../functions/request";
import { ligarAtalhos } from "../../functions/atalhosTexto";
import { Button, Descriptions, Input, Select, Typography, DatePicker, Modal, Space, Alert } from "antd";
import dayjs from "dayjs";
import {
  formatDateToBR,
  optionsSection,
} from "../../functions/functionsUtility";
import { toast } from "react-toastify";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Aguarde from "../../components/aguarde/aguarde";
import { Content } from "antd/es/layout/layout";
import { FaSave, FaTrash, FaUpload } from "react-icons/fa";
import { BsCloudRainHeavy } from "react-icons/bs";
import { ExportOutlined, UpOutlined, DownOutlined, ClockCircleOutlined, CloseOutlined, ZoomInOutlined, ZoomOutOutlined, LineChartOutlined, FileTextOutlined, HistoryOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import JoditEditor from "jodit-react";
import { openPopupEditor, openReportPopupAndCapture, limparColagemDeLinhas, removerParagrafos, normalizarParaEditor, contarPalavras } from "../../functions/utils";
const { Text } = Typography;

const LOCAL_STORAGE_KEY = "editorAnaliseDraft";
const ZOOM_LEITURA_KEY = "editorRTE_zoomLeitura";

// Backup no banco (ver rb_comentario_backup.js na API): a partir de 2 mil
// palavras o texto vai para o servidor a cada 2 minutos, se mudou. A API
// guarda as 5 últimas cópias do usuário e recusa texto menor que a última
// cópia da mesma análise, para um texto apagado não tomar o lugar das boas.
const BACKUP_MIN_PALAVRAS = 2000;
const BACKUP_INTERVALO_MS = 2 * 60 * 1000;
// Identifica a análise NOVA nas cópias (a já gravada usa o com_numero). Dura
// enquanto durar o rascunho local e é trocada quando ele é descartado.
const BACKUP_CHAVE_KEY = "editorAnaliseBackupChave";

function chaveDoRascunho() {
  try {
    let chave = localStorage.getItem(BACKUP_CHAVE_KEY);
    if (!chave) {
      chave = `rasc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(BACKUP_CHAVE_KEY, chave);
    }
    return chave;
  } catch {
    return "rasc-sem-storage";
  }
}

function limparRascunho() {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(BACKUP_CHAVE_KEY);
  } catch {}
}

const REPORT_CHART_SOURCES = [
  { key: "comex", label: "Gráfico Comex", path: "/relatorios/comex-graficos" },
  { key: "usda", label: "Gráfico USDA", path: "/relatorios/usda" },
  { key: "usda-nass", label: "Gráfico USDA NASS", path: "/relatorios/usda-nass" },
  { key: "cot", label: "Posição dos Fundos (COT)", path: "/relatorios/cot" },
  { key: "ipar", label: "Gráficos IPAR", path: "/central-de-graficos" },
  { key: "gerador", label: "Gerar Gráfico", path: "/graficos/gerador" },
];

export default function EditorAnalise() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [titlePage, setTitlePage] = useState("");
  const [secao, setSecao] = useState("");
  const [content, setContent] = useState("");
  const [formattedDate, setFormattedDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldsCollapsed, setFieldsCollapsed] = useState(false);
  const [reportPickerOpen, setReportPickerOpen] = useState(false);
  const [capturedChartHtml, setCapturedChartHtml] = useState(null);
  const [agendado, setAgendado] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const editorRef = useRef(null);

  // Zoom de leitura do editor: só aumenta/diminui visualmente o conteúdo
  // (via CSS "zoom" na área de edição do Jodit), não mexe no HTML/font-size
  // salvo — é só conforto de leitura de quem está escrevendo/revisando.
  // Preferência é lembrada entre sessões (mesma chave nos dois editores).
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(ZOOM_LEITURA_KEY), 10);
      return Number.isFinite(saved) ? saved : 100;
    } catch {
      return 100;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_LEITURA_KEY, String(zoomLevel));
    } catch {}
  }, [zoomLevel]);

  // Altura da barra de botões varia (quebra pra 2+ linhas em telas
  // estreitas, já que tem flexWrap) — mede de verdade com ResizeObserver e
  // publica numa CSS var em vez de um "top" fixo, senão o toolbar do Jodit
  // (que fica sticky logo abaixo, via CSS) acaba ficando embaixo da barra
  // quando ela cresce.
  const actionBarRef = useRef(null);
  useEffect(() => {
    const el = actionBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        "--rb-action-bar-height",
        `${el.offsetHeight}px`
      );
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Atalhos de texto: mapa {atalho -> conteudo} carregado uma vez e mantido
  // num ref (não precisa re-renderizar nada quando muda, só precisa estar
  // sempre atualizado no momento em que o usuário digita).
  const atalhosRef = useRef({});
  useEffect(() => {
    request("/atalho/getAtalhosAtivos", { method: "GET" })
      .then((lista) => {
        const mapa = {};
        (lista || []).forEach((a) => {
          mapa[a.atl_atalho.toLowerCase()] = a.atl_conteudo;
        });
        atalhosRef.current = mapa;
      })
      .catch((err) => console.log("Erro ao carregar atalhos de texto:", err));
  }, []);

  // "/atalho" + espaço ou Enter troca pelo conteúdo cadastrado (lógica
  // compartilhada com o outro editor em functions/atalhosTexto.js).
  // Precisa ser estável (useCallback) — o jodit-react re-cria a instância do
  // editor sempre que a prop editorRef muda de identidade entre renders.
  const setEditorInstance = useCallback((instance) => {
    editorRef.current = instance;
    ligarAtalhos(instance, () => atalhosRef.current, (html) => {
      if (typeof html === "string") setContent(html);
    });
    if (instance?.e) {
      instance.e.off("beforePasteInsert", limparColagemDeLinhas);
      instance.e.on("beforePasteInsert", limparColagemDeLinhas);
    }
  }, []);
  const { search } = useLocation();
  const [URLSearchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessLevel } = useContext(AuthContext);
  const isUpdating = !!URLSearchParams.get("section");
  const isPopup = URLSearchParams.get("popup") === "1";

  const STORAGE_KEY = LOCAL_STORAGE_KEY;

  const fetchTableData = async () => {
    const currentPathWithQuery = id
      ? "/comentario/getcomentarios/" + id
      : isUpdating
        ? "/comentario/getcomentariobysection" + search
        : "/comentario/insertComentario" + search;

    console.log(currentPathWithQuery);
    if (!isUpdating && !id) {
      setData(null);
      setTitle("");
      setSecao("");
      setContent("");
      setFormattedDate("");
      setIsLoading(false);
      setAgendado(false);
      setShowScheduler(false);
      setScheduleDate(null);
      return;
    }
    try {
      setIsLoading(true);
      const response = await request(currentPathWithQuery, {
        method: "GET",
      });
      if (!response) {
        return;
      }
      if (response.length === 0) {
        navigate("/comentario/insertComentario");
      }

      setData(response[0]);
      setTitle(response[0]?.com_titulo || "");
      setSecao(response[0]?.com_secao || "");
      setContent(normalizarParaEditor(response[0]?.com_texto || ""));
      const date = new Date(response[0]?.com_data);
      setFormattedDate(
        `${formatDateToBR(date)} às ${date.getHours()}:${date.getMinutes()}`
      );
      setAgendado(response[0]?.com_agendado === true || response[0]?.com_agendado === 1);
      setScheduleDate(response[0]?.com_data ? dayjs(response[0]?.com_data) : null);
      setIsLoading(false);
    } catch (error) {
      console.error("Erro na requisição:", error);
    }
  };

  useEffect(() => {
    if (!isUpdating && !id) {
      setTitlePage("Inserir Análise");
    } else {
      setTitlePage(
        `Editar Análise de ${optionsSection.find((option) => {
          return option.value === secao;
        })?.label
        }`
      );
    }
  }, [secao, id, isUpdating]);

  useEffect(() => {
    fetchTableData();
  }, [search]);

  useEffect(() => {
    if (!id && !isUpdating) {
      const savedDraft = localStorage.getItem(STORAGE_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setTitle(parsed.title || "");
          setSecao(parsed.secao || "");
          setContent(parsed.content || "");
        } catch (e) {
          console.warn("Erro ao carregar rascunho do localStorage", e);
        }
      }
    }
  }, [STORAGE_KEY, id, isUpdating]);

  useEffect(() => {
    if (!id && !isUpdating) {
      const draft = {
        title,
        secao,
        content,
      };
      if (draft) localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    }
  }, [id, isUpdating, STORAGE_KEY, title, secao, content]);

  // ---- Backup no banco -------------------------------------------------
  // O estado `content` só é atualizado no blur do editor; quem escreve sem
  // sair do editor ficaria sem backup. Por isso o texto é lido direto da
  // instância do Jodit, e os demais campos por refs (o timer é criado uma
  // vez só e precisa ver sempre os valores atuais).
  const [ultimoBackup, setUltimoBackup] = useState(null);
  const [backupsAbertos, setBackupsAbertos] = useState(false);
  const [backups, setBackups] = useState([]);
  const [carregandoBackups, setCarregandoBackups] = useState(false);
  const camposRef = useRef({});
  camposRef.current = { title, secao, content, comNumero: data?.com_numero, novo: !id && !isUpdating };
  const ultimaAssinaturaRef = useRef(null);
  const enviandoBackupRef = useRef(false);

  const fazerBackup = useCallback(async () => {
    const campos = camposRef.current;
    const texto =
      typeof editorRef.current?.value === "string" ? editorRef.current.value : campos.content;
    if (contarPalavras(texto) < BACKUP_MIN_PALAVRAS) return;
    const assinatura = `${campos.comNumero ?? ""}\u0000${campos.title}\u0000${texto}`;
    if (assinatura === ultimaAssinaturaRef.current || enviandoBackupRef.current) return;
    enviandoBackupRef.current = true;
    try {
      const r = await request("/comentario/backup", {
        method: "POST",
        body: JSON.stringify({
          chave: campos.novo ? chaveDoRascunho() : undefined,
          com_numero: campos.novo ? undefined : campos.comNumero,
          titulo: campos.title,
          secao: campos.secao,
          texto,
        }),
      });
      ultimaAssinaturaRef.current = assinatura;
      const hora = dayjs().format("HH:mm");
      if (r?.salvo) setUltimoBackup({ hora, palavras: r.palavras });
      else if (/menor/.test(r?.motivo || "")) setUltimoBackup((b) => ({ ...b, recusadoMenor: hora }));
    } catch (err) {
      console.log("Backup da análise não foi gravado:", err);
    } finally {
      enviandoBackupRef.current = false;
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(fazerBackup, BACKUP_INTERVALO_MS);
    // Trocar de aba/minimizar é um bom momento: se o navegador travar
    // depois, a cópia já está lá.
    const aoEsconder = () => {
      if (document.visibilityState === "hidden") fazerBackup();
    };
    document.addEventListener("visibilitychange", aoEsconder);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", aoEsconder);
    };
  }, [fazerBackup]);

  const abrirBackups = async () => {
    setBackupsAbertos(true);
    setCarregandoBackups(true);
    try {
      setBackups((await request("/comentario/backups", { method: "GET" })) || []);
    } catch {
      toast.error("Não foi possível carregar os backups.");
    } finally {
      setCarregandoBackups(false);
    }
  };

  const restaurarBackup = async (item) => {
    let completo;
    try {
      completo = await request(`/comentario/backup/${item.bkp_codigo}`, { method: "GET" });
    } catch {
      toast.error("Não foi possível abrir o backup.");
      return;
    }
    const outraAnalise = camposRef.current.novo
      ? item.bkp_com_numero != null
      : item.bkp_com_numero !== camposRef.current.comNumero;
    Modal.confirm({
      title: "Restaurar este backup?",
      content: (
        <>
          O título e o texto do editor serão trocados pelo backup de{" "}
          {dayjs(completo.bkp_data).format("DD/MM/YYYY [às] HH:mm")} ({completo.bkp_palavras} palavras).
          {outraAnalise && (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message="Este backup é de outra análise. Ele será carregado nesta tela; confira antes de salvar."
            />
          )}
          <div style={{ marginTop: 8 }}>Nada é gravado na análise até você clicar em Salvar/Inserir.</div>
        </>
      ),
      okText: "Restaurar",
      cancelText: "Cancelar",
      onOk: () => {
        setTitle(completo.bkp_titulo || "");
        if (completo.bkp_secao != null) setSecao(completo.bkp_secao);
        setContent(completo.bkp_texto || "");
        setBackupsAbertos(false);
        toast.success("Backup restaurado no editor.");
      },
    });
  };

  function validation() {
    if (!title || title.replace(/\s+/g, "").length <= 3 || !secao) {
      toast.error("Preencha todos os campos!");
      return false;
    } else if (title.length > 80) {
      toast.error("O Título deve conter até 80 caracteres!");
      return false;
    } else {
      return true;
    }
  }

  const handleUpdate = async () => {
    // Edição de um item existente não mexe na data/status de publicação — só o
    // fluxo de Agendar Publicação (handleSchedule) faz isso.
    if (validation()) {
      const newData = {
        com_numero: data.com_numero,
        com_titulo: title,
        com_secao: secao,
        com_texto: removerParagrafos(content),
      };
      request("/comentario/updateComentario", {
        method: "PUT",
        body: JSON.stringify({ newData: newData }),
      }).then(() => {
        toast.success("Análise atualizada com sucesso!");
      });
    }
  };
  const handleInsertApproval = async() => {
    // "Salvar Aprovação" sempre usa a data/hora atual.
    if (validation()) {
      const newData = {
        com_titulo: title,
        com_secao: secao,
        com_texto: removerParagrafos(content),
        com_ativo: 0,
        com_agendado: 0,
        com_data: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };
      request("/comentario/insertComentario", {
        method: "POST",
        body: JSON.stringify({ newData: newData }),
      }).then(() => {
        toast.success("Análise inserida com sucesso!");
        limparRascunho();
      });
    }
  };
  const handleInsert = async(aprovacao) => {
    // "Inserir" (publicar) sempre usa a data/hora atual.
    if (validation()) {
      const newData = {
        com_titulo: title,
        com_secao: secao,
        com_texto: removerParagrafos(content),
        com_ativo: 1,
        com_agendado: 0,
        com_data: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };
      request("/comentario/insertComentario", {
        method: "POST",
        body: JSON.stringify({ newData: newData }),
      }).then(() => {
        toast.success("Análise inserida com sucesso!");
        limparRascunho();
      });
    }
  };

  const handleSchedule = async (dataAgendada) => {
    if (!dataAgendada) {
      toast.error("Selecione a data e hora do agendamento!");
      return;
    }
    if (!validation()) return;

    const com_data = dayjs(dataAgendada).format("YYYY-MM-DD HH:mm:ss");
    const isEditing = !!formattedDate;
    const comTextoLimpo = removerParagrafos(content);
    const newData = isEditing
      ? { com_numero: data.com_numero, com_titulo: title, com_secao: secao, com_texto: comTextoLimpo, com_ativo: 0, com_agendado: 1, com_data }
      : { com_titulo: title, com_secao: secao, com_texto: comTextoLimpo, com_ativo: 0, com_agendado: 1, com_data };

    request(isEditing ? "/comentario/updateComentario" : "/comentario/insertComentario", {
      method: isEditing ? "PUT" : "POST",
      body: JSON.stringify({ newData }),
    }).then(() => {
      toast.success(`Análise agendada para ${dayjs(dataAgendada).format("DD/MM/YYYY HH:mm")}!`);
      limparRascunho();
      setShowScheduler(false);
    });
  };

  /**
   * Publica na hora uma análise que estava agendada.
   *
   * Salva o texto atual antes de publicar: quem abriu a agendada para
   * revisar e clicou em publicar espera que a versão da tela seja a que
   * vai ao ar, não a que estava gravada.
   *
   * A data passa a ser a da publicação real (o endpoint carimba GETDATE()),
   * senão a análise sairia com a data futura do agendamento.
   */
  const handlePublicarAgora = async () => {
    if (!validation()) return;
    if (
      !window.confirm(
        "Publicar esta análise agora? A data passa a ser a de agora e o agendamento é cancelado."
      )
    )
      return;

    try {
      await request("/comentario/updateComentario", {
        method: "PUT",
        body: JSON.stringify({
          newData: {
            com_numero: data.com_numero,
            com_titulo: title,
            com_secao: secao,
            com_texto: removerParagrafos(content),
          },
        }),
      });
      await request(`/comentario/publicarAgoraComentario/${data.com_numero}`, {
        method: "PUT",
      });
      toast.success("Análise publicada agora!");
      setAgendado(false);
      limparRascunho();
    } catch (err) {
      toast.error("Não foi possível publicar a análise.");
    }
  };

  const deleteItem = () => {
    if (
      window
        .confirm(
          "Você tem certeza que deseja excluir essa análise? Essa ação será irreversível."
        )
        .valueOf() === true
    ) {
      const currentPathWithQuery = `/comentario/deleteComentario/${data?.com_numero}`;
      request(currentPathWithQuery, {
        method: "DELETE",
      }).then(() => {
        toast.success("Analise deletada com sucesso!");
        setTimeout(() => {
          window.location.href = "/comentario";
        }, 2000);
      });
    }
  };

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
  };
  const handleSecaoChange = (value) => {
    setSecao(value);
  };

  const insertIntoBody = (html) => {
    const jodit = editorRef.current;
    if (jodit?.s) {
      // O Jodit sempre joga conteúdo em BLOCO (div/iframe/p) pro final do
      // editor, ignorando o cursor — só respeita a posição pra conteúdo
      // inline. Contorna isso inserindo um marcador inline no cursor (que É
      // respeitado) e depois trocando ele pelo HTML real direto no DOM.
      const markerId = `rb-insert-marker-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      jodit.s.insertHTML(`<span id="${markerId}">​</span>`);
      const marker = document.getElementById(markerId);
      if (marker) {
        marker.outerHTML = html;
      } else {
        jodit.s.insertHTML(html);
      }
      if (typeof jodit.value === "string") {
        setContent(jodit.value);
      }
    } else {
      setContent((prev) => (prev || "") + html);
    }
  };

  // Mesma base (banco de imagens) usada no editor de Notícias — insere no
  // corpo da análise, pode ser usado quantas vezes precisar.
  const openPopupForGrafico = () => {
    const popup = window.open(
      "/multimidia/banco_imagens_popup",
      "",
      "popup,width=925,height=600"
    );

    window.onChooseImage = (data) => {
      const url = `${process.env.REACT_APP_BANCO_IMG_URL}${data.img_arquivo}`;
      const html = `<p><img src="${url}" alt="${data.img_nome || ""}" style="max-width:100%;" /></p>`;
      // Mesma correção do "Gráfico de Relatório": não insere direto, deixa o
      // usuário escolher Inserir (no cursor salvo antes de abrir o popup) ou
      // só Copiar.
      setCapturedChartHtml(html);
      setReportPickerOpen(true);
      popup.close();
    };
  };

  const handleInsertReportChart = (html) => {
    // Não insere direto — guarda o código e deixa o usuário escolher Inserir
    // (no cursor salvo antes de abrir a janela do relatório) ou só Copiar.
    setCapturedChartHtml(html);
    setReportPickerOpen(true);
  };

  const CURSOR_ANCHOR_ID = "rb-cursor-anchor";

  // mousedown (não click) porque o próprio clique já tira o foco/seleção do
  // editor antes do handler de click rodar — precisa marcar a posição do
  // cursor ANTES disso acontecer. Usa um marcador inline de verdade no DOM
  // (não o save()/restore() do Jodit — não sobrevive de forma confiável à
  // troca de foco pra janela do relatório e de volta).
  const saveEditorCursor = () => {
    document.getElementById(CURSOR_ANCHOR_ID)?.remove();
    editorRef.current?.s?.insertHTML(`<span id="${CURSOR_ANCHOR_ID}">​</span>`);
  };

  const openReportPicker = () => {
    setReportPickerOpen(true);
  };

  const openReportChartSource = (path) => {
    setReportPickerOpen(false);
    openReportPopupAndCapture(path, handleInsertReportChart);
  };

  const confirmInsertReportChart = () => {
    if (!capturedChartHtml) return;
    const anchor = document.getElementById(CURSOR_ANCHOR_ID);
    if (anchor) {
      anchor.outerHTML = capturedChartHtml;
      if (typeof editorRef.current?.value === "string") {
        setContent(editorRef.current.value);
      }
    } else {
      insertIntoBody(capturedChartHtml);
    }
    toast.success("Gráfico adicionado à matéria!");
    setCapturedChartHtml(null);
    setReportPickerOpen(false);
  };

  const confirmCopyReportChart = () => {
    if (!capturedChartHtml) return;
    navigator.clipboard.writeText(capturedChartHtml)
      .then(() => toast.success("Código copiado para a área de transferência!"))
      .catch(() => toast.error("Erro ao copiar."));
  };

  const closeReportPicker = () => {
    document.getElementById(CURSOR_ANCHOR_ID)?.remove();
    setCapturedChartHtml(null);
    setReportPickerOpen(false);
  };

  // Config precisa de identidade estável entre renders — o jodit-react
  // destrói e recria a instância inteira do editor sempre que a referência
  // de `config` muda (efeito com deps [config, editorRef]). Como o onBlur
  // chama setContent (causando re-render), um objeto inline aqui fazia o
  // editor ser recriado toda vez que perdia o foco, resetando o scroll pro
  // topo e perdendo o lugar onde se estava editando.
  const joditConfig = useMemo(
    () => ({
      askBeforePasteHTML: true,
      //style: { fontFamily: "Arial" },
      language: "pt_br",
      // enter:"br" foi tentado aqui pra desabilitar o uso de <p> ao dar
      // Enter, mas causa perda de conteúdo: ao desfazer uma lista (clicar
      // de novo no botão de lista), o texto do último item simplesmente
      // desaparece em vez de virar parágrafo — bug confirmado do Jodit
      // quando enter="br" (não acontece com o padrão "p"). Revertido.
      width: "100%",
      minHeight: "400px",
      // O sticky nativo do Jodit escuta o scroll da JANELA (window), mas
      // quem rola de verdade aqui é o <Layout> interno com overflowY:auto
      // (ver AppLayout.js) — o cálculo do Jodit fica incompatível com isso e
      // ele acaba grudando o próprio toolbar em cima da barra de botões
      // (Inserir, Aprovação etc.), que também é sticky. Desliga o sticky
      // nativo do Jodit pra não colidir com a nossa barra.
      toolbarSticky: false,
      // Adicionar corretor ortográfico
      spellcheck: true,
      // Configurações de correção ortográfica
      spellcheckLang: 'pt-BR', // Define o idioma para português brasileiro
      cleanHTML: {
        // Permitir apenas certas tags (todas as outras serão removidas)
        // Cuidado: Se você definir 'allowTags', apenas as tags listadas serão permitidas.
        // Exemplo:
        // allowTags: {
        //     p: true,
        //     strong: true,
        //     em: true,
        //     a: { href: true, target: true },
        //     ul: true,
        //     ol: true,
        //     li: true,
        //     br: true
        // },

        // Negar certas tags (elas serão removidas)
        //denyTags: {
        //    style: false,   // Remove tags <style>     // Remove tags <p>
        //},

        // Substituir tags antigas por novas
        replaceOldTags: {
          'i': 'em',         // Substitui <i> por <em>
          'b': 'strong',     // Substitui <b> por <strong>
          //'font': 'span'     // Substitui <font> por <span>
        },
        /* cleanAttributes: {
            // Remove o atributo 'style' de todas as tags
            '*': ['style']
        },*/
        removeEmptyElements: true, // Remove elementos vazios
        fillEmptyParagraph: true, // Preencher parágrafos vazios
        removeOnError: true, // Remove elementos que não podem ser limpos
        // Limpar atributos específicos de todas as tags ou de tags específicas
        //clearObjects: true, // Limpa objetos (embeds, iframes, etc.) se não permitidos
      },
      // Forçar todos os links a terem target="_blank"
      // safeHTML: true, // Habilita a sanitização de HTML (útil para prevenir XSS)
      processPasteHTML: true,
      /* events: {
         onCustomPasteHTMLOption: (action, html) => {
           if (action === 'removeFont') {
             let htmlCleaned = html.replace(/<font[^>]*>/gi, '<font');
             htmlCleaned = htmlCleaned.replace(/<span[^>]*>/gi, '<span>');
             htmlCleaned = htmlCleaned.replace(/<p[^>]*>/gi, '<p>');
             console.log("htmlCleaned:", htmlCleaned);
             return htmlCleaned;
           }
           return html;
         }
       },
       pasteHTMLActionList: [
         { text: 'Copiar Conteúdo', value: 'removeFont' }
       ]*/
    }),
    []
  );

  return (
    <Content
      style={{
        // O Layout pai é flex-column com overflow:auto e altura fixa
        // (100vh). O Content é flex:1 1 auto — sem flexShrink:0 ele é
        // espremido pra caber exatamente na altura do container (720px
        // por ex.), mesmo tendo mais conteúdo que isso (overflow:visible
        // só deixa o excesso vazar visualmente, sem alargar a própria
        // caixa). Como o sticky é limitado à caixa do próprio pai, a
        // barra de botões "acaba o espaço" e some ao rolar, apesar da
        // rolagem real (no Layout avô) continuar bem além disso.
        // flexShrink:0 faz o Content crescer pra altura real do conteúdo.
        flexShrink: 0,
        width: "100%",
      }}
    >
      {/* O sticky nativo do Jodit (toolbarSticky) escuta o scroll da JANELA,
          mas quem rola aqui é o <Layout> interno com overflowY:auto — por
          isso ele fica desligado (toolbarSticky:false no config) e o toolbar
          do Jodit vira sticky "na mão" via CSS puro, empilhado logo abaixo
          da barra de botões (que também é sticky), em vez de ficar escondido
          atrás dela ao rolar. */}
      <style>{`
        .rb-editor-sticky-toolbar .jodit-toolbar__box {
          position: sticky;
          top: var(--rb-action-bar-height, 45px);
          z-index: 90;
          background: #fff;
        }
        /* Zoom de leitura: só escala visualmente a área de edição (via
           CSS zoom), o HTML/font-size salvo no conteúdo não é alterado. */
        .rb-editor-sticky-toolbar .jodit-wysiwyg {
          zoom: var(--rb-editor-zoom, 1);
        }
        /* Fidelidade com o que vai ao ar: o texto é salvo com as linhas
           separadas por <br>, que não tem margem nenhuma — mas dentro do
           editor o Jodit usa <p>, que vem com margem do navegador. Era essa
           margem que dava o espaço "fantasma": o redator via o texto arejado
           aqui e ele saía grudado no site. Zerando a margem, a área de edição
           mostra exatamente o resultado final. Para separar blocos, o Enter
           duplo cria uma linha em branco de verdade, que é preservada até o
           ar (ver removerParagrafos/normalizarParaEditor em functions/utils). */
        .rb-editor-sticky-toolbar .jodit-wysiwyg p {
          margin: 0;
        }
      `}</style>
      <Modal
        title={capturedChartHtml ? "Inserir Conteúdo" : "Inserir Gráfico de Relatório"}
        open={reportPickerOpen}
        onCancel={closeReportPicker}
        footer={null}
      >
        {capturedChartHtml ? (
          <>
            <Typography.Paragraph>Conteúdo pronto! O que deseja fazer?</Typography.Paragraph>
            <Space>
              <Button type="primary" onClick={confirmInsertReportChart}>
                Inserir no cursor
              </Button>
              <Button onClick={confirmCopyReportChart}>Copiar código</Button>
            </Space>
          </>
        ) : (
          <>
            <Space direction="vertical" style={{ width: "100%" }}>
              {REPORT_CHART_SOURCES.map((source) => (
                <Button
                  key={source.key}
                  block
                  size="large"
                  onClick={() => openReportChartSource(source.path)}
                >
                  {source.label}
                </Button>
              ))}
            </Space>
            <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
              Configure o gráfico na janela que abrir e clique em "Copiar"/"Gerar código" — volte
              aqui pra inserir no lugar onde estava o cursor ou só copiar o código.
            </Typography.Paragraph>
          </>
        )}
      </Modal>
      <Modal
        title="Backups automáticos"
        open={backupsAbertos}
        onCancel={() => setBackupsAbertos(false)}
        footer={null}
        width={640}
      >
        <Typography.Paragraph type="secondary">
          Cópias gravadas no banco enquanto você escreve, a partir de {BACKUP_MIN_PALAVRAS} palavras.
          Ficam as 5 mais recentes; texto menor que o último backup da análise não é gravado.
        </Typography.Paragraph>
        {carregandoBackups ? (
          <Aguarde />
        ) : backups.length === 0 ? (
          <Typography.Paragraph>Nenhum backup ainda.</Typography.Paragraph>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {backups.map((b) => (
              <div
                key={b.bkp_codigo}
                style={{ display: "flex", gap: 12, alignItems: "center", border: "1px solid #f0f0f0", borderRadius: 6, padding: "8px 10px" }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.bkp_titulo || "(sem título)"}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(b.bkp_data).format("DD/MM/YYYY HH:mm")} · {b.bkp_palavras} palavras ·{" "}
                    {b.bkp_com_numero ? `análise nº ${b.bkp_com_numero}` : "análise nova"}
                  </Text>
                </div>
                <Button type="primary" onClick={() => restaurarBackup(b)}>
                  Restaurar
                </Button>
              </div>
            ))}
          </Space>
        )}
      </Modal>
      {agendado && (
        <Alert
          type="info"
          showIcon
          icon={<ClockCircleOutlined />}
          message={`Agendada para publicar em ${scheduleDate ? dayjs(scheduleDate).format("DD/MM/YYYY [às] HH:mm") : ""}`}
          style={{ marginBottom: 12 }}
        />
      )}
      <div
        ref={actionBarRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#fff",
          padding: "6px 10px",
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          borderBottom: "1px solid #f0f0f0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
        }}
      >
        <Button
          size="middle"
          icon={fieldsCollapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={() => setFieldsCollapsed((v) => !v)}
          title={fieldsCollapsed ? "Mostrar campos" : "Recolher campos"}
        />
        {!isPopup && (
          <Button
            size="middle"
            icon={<ExportOutlined />}
            onClick={openPopupEditor}
            title="Abrir em janela cheia"
          />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 2, border: "1px solid #f0f0f0", borderRadius: 6, padding: "0 2px" }}>
          <Button
            size="middle"
            type="text"
            icon={<ZoomOutOutlined />}
            onClick={() => setZoomLevel((z) => Math.max(70, z - 10))}
            title="Diminuir fonte de leitura do editor"
          />
          <span
            onClick={() => setZoomLevel(100)}
            title="Restaurar 100%"
            style={{ fontSize: 12, minWidth: 34, textAlign: "center", cursor: "pointer", userSelect: "none" }}
          >
            {zoomLevel}%
          </span>
          <Button
            size="middle"
            type="text"
            icon={<ZoomInOutlined />}
            onClick={() => setZoomLevel((z) => Math.min(200, z + 10))}
            title="Aumentar fonte de leitura do editor"
          />
        </div>
        <Button
          size="middle"
          icon={<HistoryOutlined />}
          onClick={abrirBackups}
          title={`Cópias automáticas do texto, gravadas no banco a partir de ${BACKUP_MIN_PALAVRAS} palavras`}
        >
          Backups
        </Button>
        {ultimoBackup?.hora && (
          <Text
            type="secondary"
            style={{ fontSize: 12 }}
            title={
              ultimoBackup.recusadoMenor
                ? `Às ${ultimoBackup.recusadoMenor} o texto estava menor que o último backup e não foi gravado (o backup maior foi mantido).`
                : undefined
            }
          >
            Backup às {ultimoBackup.hora}
            {ultimoBackup.recusadoMenor ? " (texto menor não gravado)" : ""}
          </Text>
        )}
        <Button
          color="purple"
          variant="solid"
          size="middle"
          icon={<LineChartOutlined />}
          onMouseDown={saveEditorCursor}
          onClick={openPopupForGrafico}
        >
          Adicionar Gráficos
        </Button>
        <Button
          color="magenta"
          variant="solid"
          size="middle"
          icon={<FileTextOutlined />}
          onMouseDown={saveEditorCursor}
          onClick={openReportPicker}
        >
          Gráfico de Relatório
        </Button>
        {/* O histórico de mapas ganhou botão próprio em vez de ser mais uma
            opção dentro de "Gráfico de Relatório": um mapa de clima não é um
            gráfico de relatório, então ninguém ia procurar por ele ali — o
            que fazia o atalho parecer inexistente. */}
        <Button
          color="blue"
          variant="solid"
          size="middle"
          icon={<BsCloudRainHeavy />}
          onMouseDown={saveEditorCursor}
          onClick={() => openReportChartSource("/clima/historico")}
        >
          Mapas de Clima
        </Button>
        {formattedDate && (
          <Button
            type="primary"
            danger
            size="middle"
            icon={<FaTrash />}
            onClick={deleteItem}
            disabled={!accessLevel?.usr_excluir}
            title={
              !accessLevel?.usr_excluir &&
              "Você não tem permissão para excluir"
            }
          >
            Excluir
          </Button>
        )}
         {accessLevel.usr_editor && !formattedDate && localStorage.getItem(STORAGE_KEY) !== '{"title":"","secao":"","content":""}' && (
            <Button
              onClick={() => { limparRascunho(); navigate(0) }}
              danger
              size="middle"
            >
              Apagar Rascunho
            </Button>)
        }
        {accessLevel.usr_editor && (
          <Button
            type="primary"
            size="middle"
            icon={<FaSave />}
            disabled={isLoading}
            onClick={formattedDate ? handleUpdate : handleInsert}
            style={{ fontWeight: "bold", marginLeft: "auto" }}
          >
            {formattedDate ? "Salvar" : "Inserir"}
          </Button>
        )}
        {/* Só aparece numa análise agendada aberta: publica na hora, com a
            data de agora, em vez de esperar o horário programado. */}
        {accessLevel.usr_editor && formattedDate && agendado && (
          <Button
            color="green"
            variant="solid"
            size="middle"
            icon={<FaUpload />}
            disabled={isLoading}
            onClick={handlePublicarAgora}
            style={{ fontWeight: "bold" }}
          >
            Publicar Agora
          </Button>
        )}
        {accessLevel.usr_editor && !formattedDate && (
           <Button
            color="cyan"
            variant="solid"
            size="middle"
            icon={<FaSave />}
            disabled={isLoading}
            onClick={handleInsertApproval}
          >
            Aprovação
          </Button>
        )}
        {accessLevel.usr_editor && (!showScheduler ? (
          <Button
            color="gold"
            variant="solid"
            size="middle"
            icon={<ClockCircleOutlined />}
            onClick={() => setShowScheduler(true)}
          >
            Agendar Publicação
          </Button>
        ) : (
          <>
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              placeholder="Data/hora do agendamento"
              value={scheduleDate}
              onChange={setScheduleDate}
              style={{ minWidth: 200 }}
            />
            <Button
              color="green"
              variant="solid"
              size="middle"
              disabled={!scheduleDate}
              onClick={() => handleSchedule(scheduleDate)}
            >
              Confirmar Agendamento
            </Button>
            <Button
              size="middle"
              icon={<CloseOutlined />}
              onClick={() => setShowScheduler(false)}
            />
          </>
        ))}
      </div>
      <Descriptions
        title={titlePage}
        bordered
        size="small"
        style={{ padding: 3 }}
      >
        {!fieldsCollapsed && (
        <>
        <Descriptions.Item
          label={"Titulo"}
          span={2}
          children={
            <Input
              type="text"
              style={{ width: "100%" }}
              value={title}
              disabled={isLoading}
              placeholder="Insira aqui o título da análise"
              onChange={handleTitleChange}
            />
          }
        // style={{ display: "flex", gap: 10, alignItems: "center" }}
        />
        <Descriptions.Item
          label={"Seção"}
          span={2}
          children={
            <Select
              style={{ flexGrow: 1, width: "100%" }}
              value={secao}
              onChange={handleSecaoChange}
              disabled={isLoading}
              loading={isLoading}
            >
              {optionsSection.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          }
        />
        <Descriptions.Item
          label={"Data"}
          size="small"
          span={2}
          children={
            <>
              {data?.com_data && (
                <Typography>
                  <Text>{formattedDate}</Text>
                </Typography>
              )}
            </>
          }
        />
        </>
        )}
      </Descriptions>
      {isLoading ? (
        <Aguarde />
      ) : (
        <div
          className="rb-editor-sticky-toolbar"
          style={{
            //maxWidth: "1000px",
            width: "100%",
            marginInline: "auto",
            padding: 3,
            "--rb-editor-zoom": zoomLevel / 100,
          }}
        >
          <JoditEditor
            editorRef={setEditorInstance}
            value={content}
            config={joditConfig}
            tabIndex={1}
            onBlur={(newContent) => setContent(newContent)}
          />
        </div>
      )}
    </Content>
  );
}
